import * as React from 'react';
import {useEffect, useState} from 'react';
import {Stack, Theme} from '@mui/material';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import {StopWatchDisplay} from './StopWatchDisplay';
import {SxProps} from '@mui/system';
import {TypographyVariant} from '@mui/material/styles/createTypography';

interface StopWatchProps {
    showControls?: boolean;
    variant?: TypographyVariant;
    sx?: SxProps<Theme>;
    inspectionMode?: 'countdown' | 'overtime';
    inspectionAllowanceMillis?: number;
    startTrigger?: React.RefObject<() => void>;
    stopTrigger?: React.RefObject<() => number>;
    resetTrigger?: React.RefObject<() => void>;
}

export function StopWatch({
    showControls = true,
    variant,
    sx,
    inspectionMode,
    inspectionAllowanceMillis = 0,
    startTrigger,
    stopTrigger,
    resetTrigger,
}: StopWatchProps) {
    const [startTime, setStartTime] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [isRunning, setIsRunning] = useState(false);

    useEffect(() => {
        if (startTrigger) {
            startTrigger.current = start;
        }
        if (stopTrigger) {
            stopTrigger.current = stop;
        }
        if (resetTrigger) {
            resetTrigger.current = reset;
        }
    });

    useEffect(() => {
        if (!isRunning || startTime === null) {
            return;
        }

        let frame = requestAnimationFrame(function tick() {
            setCurrentTime(dayjs().valueOf() - startTime);
            frame = requestAnimationFrame(tick);
        });

        return () => cancelAnimationFrame(frame);
    }, [isRunning, startTime]);

    useEffect(() => {
        if (inspectionMode === undefined) {
            return;
        }

        const inspectionTimerStart = dayjs().valueOf();
        setCurrentTime(inspectionMode === 'countdown' ? inspectionAllowanceMillis : 0);
        let frame = requestAnimationFrame(function tick() {
            const elapsedTime = dayjs().valueOf() - inspectionTimerStart;
            const displayedTime =
                inspectionMode === 'countdown'
                    ? Math.ceil(Math.max(0, inspectionAllowanceMillis - elapsedTime) / 1000) * 1000
                    : Math.floor(elapsedTime / 1000) * 1000;
            setCurrentTime(displayedTime);
            if (inspectionMode === 'overtime' || displayedTime > 0) {
                frame = requestAnimationFrame(tick);
            }
        });

        return () => cancelAnimationFrame(frame);
    }, [inspectionAllowanceMillis, inspectionMode]);

    const start = () => {
        if (!isRunning) {
            setCurrentTime(0);
            setIsRunning(true);
            setStartTime(dayjs().valueOf());
        }
    };

    const stop = () => {
        let time = 0;
        if (isRunning) {
            time = dayjs().valueOf() - startTime!;
            setIsRunning(false);
            setStartTime(null);
        }
        return time;
    };

    const reset = () => {
        stop();
        setCurrentTime(0);
    };

    return (
        <Stack direction={'column'} alignItems={'center'} sx={sx}>
            {showControls && (
                <Stack direction={'row'}>
                    <Button onClick={start}>Start</Button>
                    <Button onClick={stop}>Stop</Button>
                </Stack>
            )}
            <StopWatchDisplay currentTimeInMillis={currentTime} variant={variant} />
        </Stack>
    );
}
